import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Table } from "../../../components/Table/Table.component";
import { AdminServices } from "../../../services/admin/AdminServices";
import { auth } from "../../../config/firebase";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ReactSelect from "../ControlHub/components/ReactSelect";
import { typeOptions } from "../ControlHub/components/RegisterScreen/constants";
import { Spinner } from "../../../components/Spinner/Spinner.component";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { setCategories, setSubcategories } from "../../../store/referenceSlice";

const RECORDS_PER_PAGE = 20;

const typeOptionsWithAll = [{ label: "All", value: "ALL" }, ...typeOptions];

const PublicScreens = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [onlyLinked, setOnlyLinked] = useState(false);
  const [type, setType] = useState("ALL");
  const [isLoading, setIsLoading] = useState(false);
  const [publicScreenList, setPublicScreenList] = useState([]);
  const [pageState, setPageState] = useState({
    currentPage: 0,
    totalElements: 0,
  });
   const categoriesByCode = useSelector(
    (state: RootState) => state.reference.categoriesByCode,
  );
  const subcategoriesByCode = useSelector(
    (state: RootState) => state.reference.subcategoriesByCode,
  );

  const currentUser = auth.currentUser?.uid;

  useEffect(() => {
    fetchPublicScreeens();
  }, [pageState.currentPage]);

  const fetchPublicScreeens = async () => {
    setIsLoading(true);
    const payload = {
      page: 0,
      userId: currentUser,
    };

    try {
      const { response } = await AdminServices.getPublicScreens(payload);
      setPublicScreenList(response?.details);
      setPageState((prev) => ({
        ...prev,
        totalElements: response?.details?.totalElements,
      }));
    } catch (error) {
      console.error("Error occurred while fetching public screen list");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (
      Object.keys(categoriesByCode).length > 0 &&
      Object.keys(subcategoriesByCode).length > 0
    ) {
      return;
    }

    const fetchCategoriesAndSubcategories = async () => {
      try {
        const categoriesData = await AdminServices.fetchCategories();
        if (Array.isArray(categoriesData?.response)) {
          dispatch(setCategories(categoriesData.response));
        }

        const subCategoriesData = await AdminServices.fetchSubcategories();
        if (Array.isArray(subCategoriesData?.response)) {
          dispatch(setSubcategories(subCategoriesData.response));
        }
      } catch (error) {
        console.error("Error fetching reference data:", error);
      }
    };

    fetchCategoriesAndSubcategories();
  }, [dispatch]);

  // 🔹 Table columns redesigned
  const campaignsTableColumns = [
    {
      id: "name",
      name: "Name",
      cellRenderer: (row: any) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{row.name}</span>
          <span className="text-xs text-gray-500">{row.code}</span>
        </div>
      ),
    },
    {
      id: "type",
      name: "Type",
      cellRenderer: (row: any) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold
            ${
              row.type === "CHANNEL"
                ? "bg-gray-100 text-gray-800"
                : "bg-white text-gray-600"
            }`}
        >
          {row.type === "TV" ? "Screen" : "Channel"}
        </span>
      ),
    },
    {
      id: "store",
      name: "Store",
      cellRenderer: (row: any) =>
        row.storeName ? (
          <span className="font-medium text-gray-800">{row.storeName}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      id: "lastActive",
      name: "Last Active",
      cellRenderer: (row: any) =>
        row.lastActive ? (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">
              {row.lastActive}
            </span>
            <span className="text-xs text-gray-500">{row.lastActiveAt}</span>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      id: "actions",
      name: "Actions",
      cellRenderer: (row: any) => (
        <div className="flex gap-2">
          <button
            className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/screen/${row.id}`);
            }}
          >
            View
          </button>

          {row.linked ? (
            <button
              title="Unlink Screen"
              className="rounded-lg p-2 text-red-600 hover:bg-red-50"
              onClick={() => console.log("Unlink", row.id)}
            >
              <LinkOffIcon fontSize="small" />
            </button>
          ) : (
            <button
              title="Link Screen"
              className="rounded-lg p-2 text-green-600 hover:bg-green-50"
              onClick={() => console.log("Link", row.id)}
            >
              <LinkIcon fontSize="small" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const onPaginationClick = (start: number) => {
    const page = Math.floor(start / RECORDS_PER_PAGE);

    setPageState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  // const filteredScreenList = onlyLinked
  //   ? publicScreenList.filter((screen: any) => screen.isLinked === true)
  //   : publicScreenList;

  const filteredScreenList = publicScreenList.filter((screen: any) => {
    // 1️⃣ Linked filter
    if (onlyLinked && !screen.isLinked) return false;

    // 2️⃣ Type filter
    if (type === "TV") return screen.type === "TV";
    if (type === "CHANNEL") return screen.type === "CHANNEL";

    // 3️⃣ ALL
    return true;
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Public Screens</h2>

            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search by name or code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-xl border px-4 py-4 text-sm outline-none focus:border-gray-400"
              />

              <ReactSelect
                // label="Select Type"
                options={typeOptionsWithAll}
                value={typeOptionsWithAll.find((o) => o.value === type) || null}
                onChange={(option) => {
                  setType(String(option?.value || "ALL"));
                  setPageState((prev) => ({
                    ...prev,
                    currentPage: 0,
                  }));
                }}
                placeholder="Select Type"
              />

              <button
                onClick={() => setOnlyLinked((prev) => !prev)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold border
                  ${
                    onlyLinked
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-700"
                  }`}
              >
                Linked
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="mx-auto max-w-6xl px-6 py-6">
          <Table
            data={filteredScreenList}
            columns={campaignsTableColumns}
            totalRecords={pageState?.totalElements}
            startRecord={pageState.currentPage * RECORDS_PER_PAGE}
            onPaginationClick={onPaginationClick}
            emptyState="No screens found"
            isScrollable
          />
          {/* </div> */}
        </div>
      )}
    </div>
  );
};

export default PublicScreens;
